import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../../context/OnboardingContext'
import { useAuth } from '../../context/AuthContext'
import { n8nApi } from '../../services/n8nApi'

// Helper to sanitize values that might be "undefined" strings from Airtable
const sanitizeValue = (value, fallback = '') => {
  if (!value || value === 'undefined' || value === 'null') {
    return fallback
  }
  return value
}

const InterestPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { transitionToStage, updateProspectData, prospectData, STAGES } = useOnboarding()
  
  const [formData, setFormData] = useState({
    fullName: sanitizeValue(prospectData.fullName),
    companyName: sanitizeValue(prospectData.companyName),
    email: sanitizeValue(prospectData.email) || sanitizeValue(user?.email),
    phone: sanitizeValue(prospectData.phone),
    demoPreference: sanitizeValue(prospectData.demoPreference, null)
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    const hasFullName = formData.fullName.trim() !== ''
    const hasCompanyName = formData.companyName.trim() !== ''
    const hasEmail = formData.email.trim() !== ''
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    const hasDemoPreference = formData.demoPreference !== null
    
    return hasFullName && hasCompanyName && hasEmail && hasValidEmail && hasDemoPreference
  }, [formData])

  const validate = () => {
    const newErrors = {}
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    if (!formData.demoPreference) {
      newErrors.demoPreference = 'Please select a demo preference'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Double-check validation at submission time (belt and suspenders)
    if (!validate()) {
      console.log('Form validation failed:', errors)
      return
    }

    // Extra guard: block if form is invalid
    if (!isFormValid) {
      console.log('Form is not valid, blocking submission')
      return
    }
    
    setIsSubmitting(true)
    setErrors({})
    
    try {
      // Call n8n webhook to submit demo selection
      const response = await n8nApi.submitDemoSelection(user.uid, {
        full_name: formData.fullName,
        company_name: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        demo_preference: formData.demoPreference
      })
      
      if (response.success) {
        // Update local state
        updateProspectData(formData)
        
        // Determine next stage based on demo preference
        // CONSENT_INITIATE is the consent form page, GENERIC_DEMO is the generic demo
        const nextStage = formData.demoPreference === 'personalised' 
          ? STAGES.CONSENT_INITIATE 
          : STAGES.GENERIC_DEMO
        
        const nextPath = formData.demoPreference === 'personalised'
          ? '/onboarding/consent'
          : '/onboarding/generic-demo'
        
        const success = await transitionToStage(nextStage)
        
        if (success) {
          navigate(nextPath)
        }
      } else {
        setErrors({ submit: 'Failed to save your information. Please try again.' })
      }
    } catch (error) {
      console.error('Error submitting demo selection:', error)
      setErrors({ submit: 'An error occurred. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">
          Let's Confirm Your Interest
        </h1>
        <p className="mt-3 text-slate-600">
          To generate a personalized demo, we'll need temporary access to your dashcam
          platform to fetch sample data. First, let us know a bit about you.
        </p>
      </div>

      <div className="card">
        {errors.submit && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="label">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleChange}
              className={`input-field ${errors.fullName ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
              placeholder="Jane Smith"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="label">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              value={formData.companyName}
              onChange={handleChange}
              className={`input-field ${errors.companyName ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
              placeholder="Acme Transport Ltd"
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="label">
              Work Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={`input-field ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
              placeholder="jane@acmetransport.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Phone (Optional) */}
          <div>
            <label htmlFor="phone" className="label">
              Phone Number <span className="text-slate-400">(optional)</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="+44 7700 900000"
            />
          </div>

          {/* Demo Preference */}
          <div>
            <label className="label">
              How would you like to see FleetVision? <span className="text-red-500">*</span>
            </label>
            <div className="mt-3 space-y-3">
              <label 
                className={`
                  flex items-start p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.demoPreference === 'personalised' 
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200' 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="demoPreference"
                  value="personalised"
                  checked={formData.demoPreference === 'personalised'}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="font-medium text-slate-900">Yes, build a personalized demo</span>
                  <p className="text-sm text-slate-500 mt-0.5">
                    We'll use your actual fleet data to show exactly what insights you'll get
                  </p>
                </div>
              </label>

              <label 
                className={`
                  flex items-start p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.demoPreference === 'generic' 
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200' 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="demoPreference"
                  value="generic"
                  checked={formData.demoPreference === 'generic'}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="font-medium text-slate-900">No, I prefer a generic walkthrough</span>
                  <p className="text-sm text-slate-500 mt-0.5">
                    View a pre-recorded demo with sample data
                  </p>
                </div>
              </label>
            </div>
            {errors.demoPreference && (
              <p className="mt-2 text-sm text-red-600">{errors.demoPreference}</p>
            )}
          </div>

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              style={{
                backgroundColor: isFormValid && !isSubmitting ? '#2563eb' : '#cbd5e1',
                color: isFormValid && !isSubmitting ? 'white' : '#64748b',
                cursor: isFormValid && !isSubmitting ? 'pointer' : 'not-allowed',
                opacity: 1
              }}
              className="w-full inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg transition-all duration-200 ease-in-out"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                'Continue'
              )}
            </button>
            {!isFormValid && (
              <p className="mt-3 text-sm text-slate-500 text-center">
                Please complete all required fields to continue
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default InterestPage
